import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import {
    IonPage,
    IonContent,
    IonCard,
    IonCardContent,
    IonButton,
    IonLabel,
    IonItem,
    IonRadioGroup,
    IonRadio,
    IonText,
    IonAlert,
    IonModal,
} from "@ionic/react";

interface QuestionData {
    title: string;
    content: string;
    questions: QuestionSubData[];
}

interface QuestionSubData {
    questionText: string;
    correctAnswer: string;
    options: string[];
}

const Comprehension: React.FC = () => {
    // Dynamic story and questions

    const [currentIndex, setCurrentIndex] = useState(0);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [selectedOption, setSelectedOption] = useState<string | null>(null);
    const [showImage, setShowImage] = useState(false);
    const [questions, setQuestions] = useState<QuestionData[]>([]);
    const [userAnswer, setUserAnswer] = useState("");
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [error, setError] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);

    const shuffleArray = (array: any[]) => {
        for (let i = array.length - 1; i > 0; i--) {
            const randomIndex = Math.floor(Math.random() * (i + 1));
            [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
        }
        return array;
    };

    // Handle Submit Answer
    const handleSubmit = (selectedOption: any) => {
        setSelectedOption(selectedOption)
        if (selectedOption === questions[currentIndex]?.questions[currentQuestionIndex].correctAnswer) {

            if (currentQuestionIndex < questions[currentIndex]?.questions.length - 1) {
                setTimeout(() => {
                    setCurrentQuestionIndex((prevIndex) => prevIndex + 1);
                }, 1000); // Delay reduced to 3 seconds
            } else {
                setShowImage(true);
                console.log(currentIndex);
                setTimeout(() => {
                    if (currentIndex === questions.length - 1) {
                        setCurrentIndex(0);
                        fetchQuestions();
                    }
                    else {
                        setCurrentIndex(currentIndex + 1);
                    }
                    setError(false);
                    setCurrentQuestionIndex(0);
                    setShowImage(false);
                }, 5000); // Delay reduced to 3 seconds
            }
            setUserAnswer('');
        }
    };

    const fetchQuestions = async () => {
        try {
            let data: QuestionData[] = [];

            if (process.env.REACT_APP_DEVELOPMENT !== 'WEB') {
                const response = await fetch(process.env.REACT_APP_API_URL + 'ReadingComprehensionWorksheet.json');
                data = await response.json();
            } else {
                const module = await import('../../../datas/ReadingComprehensionWorksheet.json');
                data = module.default;
            }

            const shuffledQuestions = shuffleArray(data);
            setQuestions(shuffledQuestions);
        } catch (error) {
            console.error('Error loading data:', error);
        }
    };

    useEffect(() => {
        fetchQuestions();
        document.title = "Comprehension Worksheets for UKG | Reading Practice for Young Learners"; // Set page title
    }, []);

    return (
        <>
            <Helmet>
                <meta name="description" content='Enhance your UKG child’s reading skills with simple and engaging English comprehension exercises at Mama’s Guidance, designed to improve understanding and critical thinking.' />
            </Helmet>
            {showImage === false ? (
                <>
                    <div className="contentPart showCenter fillPreposition additionWord">
                        <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                        <IonModal
                            ref={modal}
                            trigger="open-modal"
                            initialBreakpoint={1}
                            breakpoints={[0, 0.5, 0.75, 1]}
                            handleBehavior="cycle"
                            backdropDismiss={false}  // ✅ Prevents click-to-close
                            className="full-page-modal"
                        >
                            <IonContent className="ion-padding">
                                <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                <h1>Comprehension</h1>

                                <p>
                                    <strong>Reading comprehension</strong> is the ability to understand and make sense of what is being read. For UKG students, comprehension activities involve reading short passages and answering questions about them. These exercises help improve vocabulary, sentence structure, and overall reading confidence.
                                </p>

                                <h2>📖 What Is Comprehension?</h2>
                                <p>
                                    Comprehension means understanding the meaning of a passage or story. In UKG, children read or listen to short texts and then answer questions related to them.
                                </p>

                                <h2>🎯 Learning Objectives</h2>
                                <ul>
                                    <li>Develop reading and listening skills</li>
                                    <li>Understand simple stories and information</li>
                                    <li>Answer <strong>‘what’, ‘who’, ‘where’</strong> questions</li>
                                    <li>Improve vocabulary and sentence formation</li>
                                </ul>

                                <h2>🖍️ Activities in the Worksheets</h2>
                                <ul>
                                    <li>Read a short passage and answer MCQs</li>
                                    <li>True or False based on the text</li>
                                    <li>Fill in the blanks using passage words</li>
                                    <li>Choose the correct title or picture</li>
                                    <li>Answer simple WH-questions</li>
                                </ul>

                                <h2>👩‍🏫 Tips for Parents & Teachers</h2>
                                <ul>
                                    <li>Read aloud stories daily and ask questions after</li>
                                    <li>Underline keywords and help your child guess meaning</li>
                                    <li>Practice comprehension using storybooks or picture books</li>
                                    <li>Let children retell the story in their own words</li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/ukg/english-language/articles"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Articles</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/english-language/one-and-many"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>One and Many</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/english-language/rhyming-words"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Rhyming Words</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🌟 Mama’s Guidance Tip</h2>
                                <p>
                                    Make comprehension fun by reading short stories together. After reading, ask questions like <strong>“What did the boy do?”</strong> or <strong>“Where was the cat?”</strong>
                                </p>

                                <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                            </IonContent>
                        </IonModal>
                        <article className="visually-hidden">
                            <h1>Comprehension</h1>

                            <p>
                                <strong>Reading comprehension</strong> is the ability to understand and make sense of what is being read. For UKG students, comprehension activities involve reading short passages and answering questions about them. These exercises help improve vocabulary, sentence structure, and overall reading confidence.
                            </p>

                            <h2>📖 What Is Comprehension?</h2>
                            <p>
                                Comprehension means understanding the meaning of a passage or story. In UKG, children read or listen to short texts and then answer questions related to them.
                            </p>

                            <h2>🎯 Learning Objectives</h2>
                            <ul>
                                <li>Develop reading and listening skills</li>
                                <li>Understand simple stories and information</li>
                                <li>Answer <strong>‘what’, ‘who’, ‘where’</strong> questions</li>
                                <li>Improve vocabulary and sentence formation</li>
                            </ul>

                            <h2>🖍️ Activities in the Worksheets</h2>
                            <ul>
                                <li>Read a short passage and answer MCQs</li>
                                <li>True or False based on the text</li>
                                <li>Fill in the blanks using passage words</li>
                                <li>Choose the correct title or picture</li>
                                <li>Answer simple WH-questions</li>
                            </ul>

                            <h2>👩‍🏫 Tips for Parents & Teachers</h2>
                            <ul>
                                <li>Read aloud stories daily and ask questions after</li>
                                <li>Underline keywords and help your child guess meaning</li>
                                <li>Practice comprehension using storybooks or picture books</li>
                                <li>Let children retell the story in their own words</li>
                            </ul>


                            <h2>🔗 Related Topics</h2>
                            <div>
                                <IonItem routerLink="/ukg/english-language/articles"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Articles</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/ukg/english-language/one-and-many"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>One and Many</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/ukg/english-language/rhyming-words"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Rhyming Words</IonLabel>
                                </IonItem>
                            </div>

                            <h2>🌟 Mama’s Guidance Tip</h2>
                            <p>
                                Make comprehension fun by reading short stories together. After reading, ask questions like <strong>“What did the boy do?”</strong> or <strong>“Where was the cat?”</strong>
                            </p>
                        </article>
                        <h3>{questions[currentIndex]?.title}</h3>
                        <p>
                            <strong>Story:</strong>
                            <br />
                            <IonText>{questions[currentIndex]?.content}</IonText>
                        </p>

                        <h4>{questions[currentIndex]?.questions[currentQuestionIndex]?.questionText}</h4>
                        <div className="mb-5">
                            <IonRadioGroup
                                value={selectedOption}
                                onIonChange={(e) => handleSubmit(e.detail.value)}
                            >
                                {questions[currentIndex]?.questions[currentQuestionIndex]?.options.map((option, index) => (
                                    <IonItem
                                        className={` ${selectedOption === option
                                            ? option === questions[currentIndex]?.questions[currentQuestionIndex]?.correctAnswer
                                                ? "correct"
                                                : "errorItems"
                                            : ""
                                            }`}
                                        key={index}>
                                        <IonLabel>{option}</IonLabel>
                                        <IonRadio slot="start" value={option} />
                                    </IonItem>
                                ))}
                            </IonRadioGroup>
                        </div>
                    </div>
                </>
            ) : (
                <>
                    <div className="contentPart showMiddle">
                        <img
                            style={{ height: "150px" }}
                            className="img-fluid"
                            src={process.env.REACT_APP_WELDONE_LINK}
                        />
                    </div>
                </>
            )}
        </>
    );
};

export default Comprehension;
